import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  disconnectMailbox,
  listMailboxes,
  setPrimaryMailbox,
  startMicrosoftOAuth,
  updateMailboxLimits,
} from "@/api/mailboxes";
import { startGoogleOAuth } from "@/api/google-calendar";
import {
  SettingsPageHeader,
  SettingsSection,
} from "@/components/settings/settings-ui";

export function SettingsMailboxesPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mailboxes"],
    queryFn: listMailboxes,
  });

  useEffect(() => {
    if (params.get("google") === "connected") {
      setBanner("Gmail mailbox connected.");
    } else if (params.get("google") === "needs_reconsent") {
      setBanner("Gmail linked — reconnect to grant send if needed.");
    } else if (params.get("microsoft") === "connected") {
      setBanner("Outlook / Microsoft 365 mailbox connected.");
    } else if (params.get("microsoft") === "error") {
      setBanner(params.get("message") || "Microsoft connect failed.");
    }
  }, [params]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["mailboxes"] });

  const googleConnect = useMutation({
    mutationFn: startGoogleOAuth,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  const msConnect = useMutation({
    mutationFn: startMicrosoftOAuth,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: unknown) => {
      setBanner(err instanceof Error ? err.message : "Outlook connect failed");
    },
  });

  const primaryMutation = useMutation({
    mutationFn: setPrimaryMailbox,
    onSuccess: () => {
      invalidate();
      setBanner("Primary mailbox updated.");
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectMailbox,
    onSuccess: () => {
      invalidate();
      setBanner("Mailbox disconnected.");
    },
  });

  const limitsMutation = useMutation({
    mutationFn: ({
      id,
      daily,
      hourly,
      delay,
    }: {
      id: string;
      daily?: number;
      hourly?: number;
      delay?: number;
    }) =>
      updateMailboxLimits(id, {
        dailySendLimit: daily,
        hourlySendLimit: hourly,
        delayBetweenEmailsSec: delay,
      }),
    onSuccess: () => invalidate(),
  });

  const mailboxes = data?.mailboxes ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Mailboxes"
        description="Connect multiple Gmail and Outlook mailboxes. Primary is used for Sequences by default; Emails hub can send from any."
      />

      {banner ? (
        <p className="mb-4 text-sm text-muted-foreground">{banner}</p>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => googleConnect.mutate()}
          disabled={googleConnect.isPending || data?.googleConfigured === false}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          Connect Gmail
        </button>
        <button
          type="button"
          onClick={() => msConnect.mutate()}
          disabled={
            msConnect.isPending || data?.microsoftConfigured === false
          }
          className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Connect Outlook
        </button>
        <Link
          to="/settings/deliverability-suite/overview"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
        >
          Deliverability suite
        </Link>
        <Link to="/emails" className="rounded-md border border-border px-3 py-1.5 text-sm">
          Open Emails
        </Link>
      </div>

      <SettingsSection
        title="My mailboxes"
        description={`${mailboxes.length} connected — set daily limits and primary.`}
      >
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : mailboxes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No mailboxes yet. Connect Gmail or Outlook above.
          </p>
        ) : (
          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Mailbox</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Daily</th>
                  <th className="px-3 py-2">Hourly</th>
                  <th className="px-3 py-2">Delay (s)</th>
                  <th className="px-3 py-2">Sent 24h</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {mailboxes.map((m) => (
                  <tr key={m.mailboxId} className="border-b border-border/70">
                    <td className="px-3 py-2 font-medium">
                      {m.emailAddress}
                      {m.isPrimary ? (
                        <span className="ml-2 text-xs text-brand">(primary)</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 capitalize">{m.provider}</td>
                    <td className="px-3 py-2 capitalize">{m.status}</td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        className="w-16 rounded border border-border bg-background px-1 py-0.5"
                        defaultValue={m.dailySendLimit ?? 50}
                        onBlur={(e) =>
                          limitsMutation.mutate({
                            id: m.mailboxId,
                            daily: Number(e.target.value) || 50,
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        className="w-16 rounded border border-border bg-background px-1 py-0.5"
                        defaultValue={m.hourlySendLimit ?? 6}
                        onBlur={(e) =>
                          limitsMutation.mutate({
                            id: m.mailboxId,
                            hourly: Number(e.target.value) || 6,
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        className="w-16 rounded border border-border bg-background px-1 py-0.5"
                        defaultValue={m.delayBetweenEmailsSec ?? 60}
                        onBlur={(e) =>
                          limitsMutation.mutate({
                            id: m.mailboxId,
                            delay: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {m.sentLast24h ?? 0}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      {!m.isPrimary ? (
                        <button
                          type="button"
                          className="text-xs text-brand hover:underline"
                          onClick={() => primaryMutation.mutate(m.mailboxId)}
                        >
                          Set primary
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => disconnectMutation.mutate(m.mailboxId)}
                      >
                        Disconnect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsSection>
    </div>
  );
}
